import type { ReactNode, Ref } from 'react';
import { cn } from '@/lib/utils';

export interface PaperConversationShellProps {
  topBar: ReactNode;
  dockedCards?: ReactNode;
  messages: ReactNode;
  banners?: ReactNode;
  chatInput: ReactNode;
  /** Renders a "Loading conversation…" placeholder instead of the full frame. */
  loading?: boolean;
  /** Ref attached to the scrollable messages container. */
  scrollRef?: Ref<HTMLDivElement>;
  onScroll?: () => void;
}

export const PaperConversationShell = (props: PaperConversationShellProps) => {
  const hasDockedCards = Boolean(props.dockedCards);

  if (props.loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-[13px] text-[var(--fg-3)]">
        Loading conversation…
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 relative overflow-hidden">
      {props.topBar}
      <div className="flex-1 min-h-0 relative">
        <div
          ref={props.scrollRef}
          onScroll={props.onScroll}
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
          className={cn(
            'h-full min-h-0 overflow-auto overscroll-contain px-[28px] pb-[28px]',
            hasDockedCards ? 'pt-[84px] desktop:pt-[28px]' : 'pt-[28px]',
          )}
        >
          {props.messages}
        </div>
        {hasDockedCards && (
          <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none desktop:hidden">
            {props.dockedCards}
          </div>
        )}
      </div>
      {props.banners != null && (
        <div className="shrink-0 flex flex-col gap-2 px-[var(--chat-col-gutter)] py-2 empty:hidden">
          {props.banners}
        </div>
      )}
      {props.chatInput}
    </div>
  );
};
